import React, { useState, JSX } from "react";
import {
  InfoOutlined,
  CreateOutlined,
  AddPhotoAlternate,
  Attachment,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Drawer from "./Drawer";
import { moduleType, MODULE_ASSEMBLY } from "../utils/modules";
import { AssemblyEditQuantityForm } from "../modules/assembly";
import {
  allowEditProductQty,
  allowRecalculateItems,
} from "../modules/auth/selectors/userSelector";
import { useSelector } from "react-redux";
import Menu from "./Menu";
import { PopoverVirtualElement } from "@mui/material";
import {
  getDocHeaderById,
  manageFiles,
} from "../modules/api/docListsandDetails";
import PhotosCamera from "./PhotosCamera";
import AttachmentsViewer from "./AttachmentsViewer";
import Fab from "./Fab";
import { useHeaderMenuFileItemStyles } from "./css/useHeaderMenuFileItemStyles";
import useFabStyles from "./css/useFabStyles";

interface IHeaderMenu {
  open: boolean;
  onClose: () => void;
  anchorEl:
    | Element
    | (() => Element)
    | PopoverVirtualElement
    | (() => PopoverVirtualElement)
    | null
    | undefined;
  data: { [key: string]: any };
  module?: moduleType | "";
  locale: string;
  clientDbId: number;
  onHeaderUpdate?: (hdr: any) => void;
}

function HeaderMenu(props: IHeaderMenu) {
  const { open, onClose, anchorEl, data, module, locale, clientDbId } = props;
  const { t } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerData, setDrawerData] = useState<any>(null);
  const [openQtyAdj, setOpenQtyAdj] = useState(false);
  const [openCamera, setOpenCamera] = useState(false);
  const [openAttachments, setOpenAttachments] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [docHeader, setDocHeader] = useState(data);

  const allowEditQty = useSelector(allowEditProductQty);
  const allowRecalculate = useSelector(allowRecalculateItems);

  const { classes } = useHeaderMenuFileItemStyles();
  const { classes: fabClasses } = useFabStyles();

  const initialValuesAssembly = React.useMemo(() => {
    if (module !== MODULE_ASSEMBLY)
      return { productQuantity: 0, editComponents: false, operationCode: "" };

    let qty = data.productQuantity ? data.productQuantity : 0;
    qty = typeof qty === "string" ? JSON.parse(qty) : qty;

    return {
      productQuantity: qty,
      editComponents: allowRecalculate,
      operationCode: data.operationCode || "",
    };
  }, [data, allowRecalculate, module]);

  const handleDocInfo = async () => {
    try {
      const response = await getDocHeaderById({
        operationCode: data?.operationCode,
        module,
        locale,
        clientDbId,
        lineType: 2,
      });

      const dataArray = response.data;

      if (Array.isArray(dataArray) && dataArray.length > 0) {
        if (
          dataArray[0].key !== undefined &&
          dataArray[0].value !== undefined
        ) {
          const newData = { schema: dataArray };
          setDrawerData(newData);
        } else {
          const newData = { ...dataArray[0] };
          delete newData.element1;
          delete newData.element2;
          delete newData.numberOfElements;
          delete newData.size;
          delete newData.pageNumber;
          delete newData.Row_number;
          delete newData.totalElements;
          delete newData.fileInsert;
          delete newData.fileDelete;
          delete newData.fileCount;

          setDrawerData(newData);
        }
        setOpenDrawer(true);
      } else {
        setDrawerData(null);
        setOpenDrawer(false);
      }
    } catch {
      setDrawerData(null);
      setOpenDrawer(false);
    }
  };

  const refreshDocHeader = async () => {
    try {
      const res = await getDocHeaderById({
        operationCode: data?.operationCode,
        module,
        locale,
        clientDbId,
      });
      const newHeader = res.data?.[0] || {};
      setDocHeader(newHeader);
      props.onHeaderUpdate?.(newHeader);
    } catch (error) {
      console.error("Nepavyko atnaujinti headerio:", error);
    }
  };

  const handleProductQty = () => setOpenQtyAdj(true);

  const loadAttachments = async () => {
    try {
      const res = await manageFiles({
        clientDbId,
        module,
        operationCode: data?.operationCode,
        oper: "L",
        locale,
      });

      if ("data" in res) {
        setAttachments(res.data || []);
      } else {
        setAttachments([]);
      }
    } catch (err) {
      console.error("Nepavyko gauti priedų:", err);
    }
  };

  const handleAttachments = async () => {
    await loadAttachments();
    setOpenAttachments(true);
  };

  const menuItems: { name: string; icon: JSX.Element; onClick: () => void }[] =
    [
      {
        name: t("documentInformation"),
        icon: <InfoOutlined />,
        onClick: handleDocInfo,
      },
    ];

  // "Pridėti nuotrauką" rodom tik jei backend leidžia
  if (Number(docHeader?.fileInsert) > 0) {
    menuItems.push({
      name: t("addPhoto"),
      icon: <AddPhotoAlternate />,
      onClick: () => setOpenCamera(true),
    });
  }

  if (module === MODULE_ASSEMBLY && allowEditQty) {
    menuItems.push({
      name: t("productAmountExchange"),
      icon: <CreateOutlined />,
      onClick: async () => handleProductQty(),
    });
  }
  // "Prisegti failai" rodom tik jei yra failų
  if (Number(docHeader?.fileCount) > 0) {
    menuItems.push({
      name: "", // turi būti string, kitaip MUI įvynioja į <p>
      icon: (
        <div className={classes.wrapper}>
          {/* Kairė pusė: ikona + tekstas */}
          <div className={classes.leftSide}>
            <Attachment style={{ fontSize: 22 }} />
            <span className={classes.text}>
              {t("attachedFiles", "Prisegti failai")}
            </span>
          </div>
          {/* Dešinė pusė: žalias apskritimas */}
          <Fab
            size="small"
            variant="menu"
            className={classes.fab}
            icon={
              <span className={fabClasses.fabMenuCount}>
                {docHeader.fileCount}
              </span>
            }
          />
        </div>
      ),
      onClick: handleAttachments,
    });
  }

  const handleCloseDrawer = () => {
    onClose();
    setOpenDrawer(false);
  };

  const handleCloseQtyAdj = () => {
    onClose();
    setOpenQtyAdj(false);
  };

  const handleCloseCamera = () => {
    onClose();
    setOpenCamera(false);
  };

  return (
    <div>
      {openDrawer && (
        <Drawer
          isOpen={openDrawer}
          onClose={handleCloseDrawer}
          data={drawerData}
        />
      )}

      {openQtyAdj && (
        <AssemblyEditQuantityForm
          open={openQtyAdj}
          onClose={handleCloseQtyAdj}
          initialValues={initialValuesAssembly}
        />
      )}

      {openAttachments && (
        <AttachmentsViewer
          open={openAttachments}
          onClose={() => {
            setOpenAttachments(false);
            onClose();
          }}
          attachments={attachments}
          clientDbId={clientDbId}
          module={module}
          operationCode={data?.operationCode}
          locale={locale}
          onRefresh={loadAttachments}
          onRefreshHeader={refreshDocHeader}
          fileDelete={docHeader?.fileDelete}
        />
      )}

      {openCamera && (
        <PhotosCamera
          open={openCamera}
          onClose={handleCloseCamera}
          clientDbId={clientDbId}
          module={module}
          operationCode={data?.operationCode || ""}
          locale={locale}
          onRefresh={refreshDocHeader}
        />
      )}

      <Menu
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        data={menuItems}
      />
    </div>
  );
}

export default HeaderMenu;
